<template>
  <main class="flex justify-center bg-gray-50">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">

      <!-- Notifications -->
      <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
        <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg">{{ errorMessage }}</div>
        <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg">{{ successMessage }}</div>
      </div>

      <!-- ===================== MODE ADMIN ===================== -->
      <template v-if="isSuper">
        <div class="w-full mb-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h1 class="text-2xl font-bold text-gray-800">Gestion des salaires</h1>
          <div class="flex flex-wrap items-center gap-2">
            <input type="month" v-model="mois" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
            <button @click="openTauxModal" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-md">
              <i class="fa-solid fa-percent mr-2"></i> Taux
            </button>
            <button v-if="fiches.length" @click="markAllAsPaid" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-600 hover:bg-orange-700 transition-colors shadow-md">
              <i class="fa-solid fa-money-check-dollar mr-2"></i> Tout payer
            </button>
          </div>
        </div>

        <!-- Récap -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p class="text-xs text-gray-500 mb-1">Employés</p>
            <p class="text-xl font-bold text-gray-800">{{ recap.nombre || 0 }}</p>
          </div>
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p class="text-xs text-gray-500 mb-1">Total commissions</p>
            <p class="text-xl font-bold text-orange-600">{{ formatMoney(recap.totalCommissions) }}</p>
          </div>
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p class="text-xs text-gray-500 mb-1">Total charges</p>
            <p class="text-xl font-bold text-red-500">{{ formatMoney(recap.totalCharges) }}</p>
          </div>
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <p class="text-xs text-gray-500 mb-1">Total à payer</p>
            <p class="text-xl font-bold text-emerald-600">{{ formatMoney(recap.totalSalaires) }}</p>
          </div>
        </div>

        <div v-if="isLoading" class="text-center h-[300px] w-full flex flex-col justify-center items-center">
          <div class="loader mb-4"></div>
          <p class="text-gray-500 font-medium">Chargement des salaires...</p>
        </div>
        <div v-else-if="fiches.length === 0" class="text-center w-full h-[300px] flex flex-col justify-center items-center">
          <i class="fa-solid fa-money-bill-wave text-4xl text-gray-300 mb-4"></i>
          <h2 class="text-lg font-semibold text-gray-500">Aucun employé</h2>
        </div>

        <div v-else>
          <!-- Tableau (desktop) -->
          <div class="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table class="w-full text-sm min-w-[980px]">
              <thead>
                <tr class="text-left text-gray-500 border-b border-gray-100">
                  <th class="px-4 py-3 font-semibold">Employé</th>
                  <th class="px-4 py-3 font-semibold">Fixe</th>
                  <th class="px-4 py-3 font-semibold text-right">iOS</th>
                  <th class="px-4 py-3 font-semibold text-right">Android</th>
                  <th class="px-4 py-3 font-semibold text-right">Access.</th>
                  <th class="px-4 py-3 font-semibold text-right">Commission</th>
                  <th class="px-4 py-3 font-semibold text-right">Charges</th>
                  <th class="px-4 py-3 font-semibold text-right">Net</th>
                  <th class="px-4 py-3 font-semibold text-center">Statut</th>
                  <th class="px-4 py-3 font-semibold text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fiche in fiches" :key="fiche._id" class="border-b border-gray-50 hover:bg-gray-50">
                  <td class="px-4 py-3">
                    <p class="font-semibold text-gray-800">{{ fiche.employe.prenoms }} {{ fiche.employe.nom }}</p>
                    <p class="text-xs text-gray-500">{{ fiche.employe.poste }} • {{ fiche.employe.boutique }}</p>
                  </td>
                  <td class="px-4 py-3">
                    <button @click="openFixeModal(fiche)" :disabled="fiche.statut !== 'en_cours'" class="flex items-center gap-2 text-gray-800 font-medium hover:text-orange-600 disabled:opacity-60 disabled:hover:text-gray-800">
                      {{ formatMoney(fiche.salaireFixe) }}
                      <i v-if="fiche.statut === 'en_cours'" class="fa-solid fa-pen text-xs text-gray-400"></i>
                    </button>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ formatMoney(fiche.ventesParCategorie.ios) }}
                    <label class="flex items-center justify-end gap-1 mt-1" :class="fiche.statut !== 'en_cours' ? 'opacity-50' : 'cursor-pointer'">
                      <span class="text-[10px] text-gray-400">commission</span>
                      <input type="checkbox" :checked="categorieActive(fiche, 'ios')" @change="toggleCategorie(fiche, 'ios')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500">
                    </label>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ formatMoney(fiche.ventesParCategorie.android) }}
                    <label class="flex items-center justify-end gap-1 mt-1" :class="fiche.statut !== 'en_cours' ? 'opacity-50' : 'cursor-pointer'">
                      <span class="text-[10px] text-gray-400">commission</span>
                      <input type="checkbox" :checked="categorieActive(fiche, 'android')" @change="toggleCategorie(fiche, 'android')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500">
                    </label>
                  </td>
                  <td class="px-4 py-3 text-right text-gray-600">
                    {{ formatMoney(fiche.ventesParCategorie.accessoires) }}
                    <label class="flex items-center justify-end gap-1 mt-1" :class="fiche.statut !== 'en_cours' ? 'opacity-50' : 'cursor-pointer'">
                      <span class="text-[10px] text-gray-400">commission</span>
                      <input type="checkbox" :checked="categorieActive(fiche, 'accessoires')" @change="toggleCategorie(fiche, 'accessoires')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500">
                    </label>
                  </td>
                  <td class="px-4 py-3 text-right">
                    <div class="flex flex-col items-end gap-1">
                      <span class="font-medium" :class="fiche.commissionAppliquee ? 'text-orange-600' : 'text-gray-400 line-through'">{{ formatMoney(fiche.commissionMontant) }}</span>
                      <button @click="toggleCommission(fiche)" :disabled="fiche.statut !== 'en_cours'"
                        :class="fiche.commissionAppliquee ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'"
                        class="text-[11px] px-2 py-0.5 rounded-full font-semibold disabled:opacity-60">
                        {{ fiche.commissionAppliquee ? 'Activée' : 'Désactivée' }}
                      </button>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-right text-red-500">{{ formatMoney(fiche.chargesEmploye) }}</td>
                  <td class="px-4 py-3 text-right">
                    <button @click="openNetModal(fiche)" :disabled="fiche.statut !== 'en_cours'" class="flex flex-col items-end disabled:opacity-70">
                      <span class="font-bold text-emerald-600 flex items-center gap-1">
                        {{ formatMoney(effectiveNet(fiche)) }}
                        <i v-if="fiche.statut === 'en_cours'" class="fa-solid fa-pen text-[10px] text-gray-400"></i>
                      </span>
                      <span v-if="isAjuste(fiche)" class="text-[10px] text-gray-400">calculé : {{ formatMoney(fiche.salaireNet) }}</span>
                    </button>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span :class="statutClass(fiche.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ statutLabel(fiche.statut) }}</span>
                    <div v-if="fiche.statut === 'paye'" class="mt-1">
                      <span v-if="fiche.recu" class="text-[10px] text-green-600 font-semibold"><i class="fa-solid fa-check"></i> Reçu</span>
                      <span v-else class="text-[10px] text-gray-400">réception en attente</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button v-if="fiche.statut !== 'paye'" @click="markAsPaid(fiche)" class="text-[11px] px-3 py-1 rounded-full font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors whitespace-nowrap">
                      <i class="fa-solid fa-money-bill-wave mr-1"></i> Payer
                    </button>
                    <button v-else @click="markAsPaid(fiche)" :class="fiche.recu ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'" class="text-[11px] px-3 py-1 rounded-full font-semibold transition-colors whitespace-nowrap">
                      <i class="fa-solid fa-rotate-left mr-1"></i> Annuler
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Cartes (mobile) -->
          <div class="lg:hidden space-y-4">
            <div v-for="fiche in fiches" :key="fiche._id" class="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div class="flex justify-between items-start mb-3">
                <div>
                  <p class="font-semibold text-gray-800">{{ fiche.employe.prenoms }} {{ fiche.employe.nom }}</p>
                  <p class="text-xs text-gray-500">{{ fiche.employe.poste }} • {{ fiche.employe.boutique }}</p>
                </div>
                <span :class="statutClass(fiche.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold whitespace-nowrap">{{ statutLabel(fiche.statut) }}</span>
              </div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div class="flex justify-between col-span-2 border-b border-gray-50 pb-2">
                  <span class="text-gray-500">Salaire fixe</span>
                  <button @click="openFixeModal(fiche)" :disabled="fiche.statut !== 'en_cours'" class="flex items-center gap-2 font-medium text-gray-800 hover:text-orange-600 disabled:opacity-60">
                    {{ formatMoney(fiche.salaireFixe) }}
                    <i v-if="fiche.statut === 'en_cours'" class="fa-solid fa-pen text-[10px] text-gray-400"></i>
                  </button>
                </div>
                <div class="flex justify-between items-center"><span class="text-gray-500">iOS</span>
                  <span class="flex items-center gap-2">
                    <span class="text-gray-700">{{ formatMoney(fiche.ventesParCategorie.ios) }}</span>
                    <input type="checkbox" :checked="categorieActive(fiche, 'ios')" @change="toggleCategorie(fiche, 'ios')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500" title="Compte dans la commission">
                  </span>
                </div>
                <div class="flex justify-between items-center"><span class="text-gray-500">Android</span>
                  <span class="flex items-center gap-2">
                    <span class="text-gray-700">{{ formatMoney(fiche.ventesParCategorie.android) }}</span>
                    <input type="checkbox" :checked="categorieActive(fiche, 'android')" @change="toggleCategorie(fiche, 'android')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500" title="Compte dans la commission">
                  </span>
                </div>
                <div class="flex justify-between items-center col-span-2 border-b border-gray-50 pb-2"><span class="text-gray-500">Accessoires</span>
                  <span class="flex items-center gap-2">
                    <span class="text-gray-700">{{ formatMoney(fiche.ventesParCategorie.accessoires) }}</span>
                    <input type="checkbox" :checked="categorieActive(fiche, 'accessoires')" @change="toggleCategorie(fiche, 'accessoires')" :disabled="fiche.statut !== 'en_cours'" class="w-3 h-3 rounded text-orange-500 focus:ring-orange-500" title="Compte dans la commission">
                  </span>
                </div>
                <div class="flex justify-between items-center col-span-2 border-b border-gray-50 pb-2">
                  <span class="text-gray-500">Commission</span>
                  <div class="flex items-center gap-2">
                    <span class="font-medium" :class="fiche.commissionAppliquee ? 'text-orange-600' : 'text-gray-400 line-through'">{{ formatMoney(fiche.commissionMontant) }}</span>
                    <button @click="toggleCommission(fiche)" :disabled="fiche.statut !== 'en_cours'" :class="fiche.commissionAppliquee ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'" class="text-[11px] px-2 py-0.5 rounded-full font-semibold disabled:opacity-60">{{ fiche.commissionAppliquee ? 'ON' : 'OFF' }}</button>
                  </div>
                </div>
                <div class="flex justify-between col-span-2 border-b border-gray-50 pb-2"><span class="text-gray-500">Charges</span><span class="text-red-500">- {{ formatMoney(fiche.chargesEmploye) }}</span></div>
                <div class="flex justify-between items-center col-span-2 pt-1">
                  <span class="font-bold text-gray-800">Net</span>
                  <button @click="openNetModal(fiche)" :disabled="fiche.statut !== 'en_cours'" class="flex items-center gap-1 font-bold text-base text-emerald-600 disabled:opacity-70">
                    {{ formatMoney(effectiveNet(fiche)) }}
                    <i v-if="fiche.statut === 'en_cours'" class="fa-solid fa-pen text-[10px] text-gray-400"></i>
                  </button>
                </div>
              </div>
              <button v-if="fiche.statut !== 'paye'" @click="markAsPaid(fiche)" class="w-full mt-3 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors">
                <i class="fa-solid fa-money-bill-wave mr-1"></i> Marquer payé
              </button>
              <div v-else class="mt-3 flex items-center gap-2">
                <span v-if="fiche.recu" class="flex-1 text-center py-2 rounded-lg text-sm font-semibold bg-green-100 text-green-700"><i class="fa-solid fa-check mr-1"></i> Reçu</span>
                <span v-else class="flex-1 text-center py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-500">Payé — réception en attente</span>
                <button @click="markAsPaid(fiche)" class="px-3 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-500 hover:bg-gray-200"><i class="fa-solid fa-rotate-left"></i></button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ===================== MODE EMPLOYÉ (lecture seule) ===================== -->
      <template v-else>
        <div class="w-full mb-6 flex justify-between items-center">
          <h1 class="text-2xl font-bold text-gray-800">Mon salaire</h1>
          <input type="month" v-model="mois" class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
        </div>

        <div v-if="isLoading" class="text-center h-[300px] w-full flex flex-col justify-center items-center">
          <div class="loader mb-4"></div>
          <p class="text-gray-500 font-medium">Chargement...</p>
        </div>

        <div v-else-if="ownFiche" class="max-w-2xl w-full">
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4">
            <div class="flex justify-between items-center mb-4">
              <div>
                <h2 class="text-lg font-bold text-gray-800">{{ ownFiche.employe.prenoms }} {{ ownFiche.employe.nom }}</h2>
                <p class="text-xs text-gray-500">{{ ownFiche.employe.poste }} • {{ ownFiche.employe.boutique }}</p>
              </div>
              <span :class="statutClass(ownFiche.statut)" class="text-[11px] px-2 py-1 rounded-full font-semibold">{{ statutLabel(ownFiche.statut) }}</span>
            </div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Salaire fixe</span><span class="font-medium text-gray-800">{{ formatMoney(ownFiche.salaireFixe) }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Ventes iOS</span><span class="text-gray-700">{{ formatMoney(ownFiche.ventesParCategorie.ios) }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Ventes Android</span><span class="text-gray-700">{{ formatMoney(ownFiche.ventesParCategorie.android) }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Ventes Accessoires</span><span class="text-gray-700">{{ formatMoney(ownFiche.ventesParCategorie.accessoires) }}</span></div>
              <div class="flex justify-between py-2 border-b border-gray-50">
                <span class="text-gray-500">Commission {{ ownFiche.commissionAppliquee ? '' : '(non appliquée)' }}</span>
                <span class="font-medium" :class="ownFiche.commissionAppliquee ? 'text-orange-600' : 'text-gray-400 line-through'">{{ formatMoney(ownFiche.commissionMontant) }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-gray-50"><span class="text-gray-500">Charges</span><span class="text-red-500">- {{ formatMoney(ownFiche.chargesEmploye) }}</span></div>
              <div class="flex justify-between py-3 mt-2"><span class="font-bold text-gray-800">Salaire net</span><span class="font-bold text-lg text-emerald-600">{{ formatMoney(ownFiche.salaireNet) }}</span></div>
            </div>

            <!-- Bouton reçu (employé) -->
            <div v-if="ownFiche.statut === 'paye'" class="mt-4">
              <button v-if="!ownFiche.recu" @click="toggleRecu" class="w-full py-2 rounded-lg text-sm font-bold bg-orange-500 text-white hover:bg-orange-600 transition-colors">
                <i class="fa-solid fa-hand-holding-dollar mr-1"></i> Marquer comme reçu
              </button>
              <div v-else class="w-full py-2 rounded-lg text-sm font-bold bg-green-100 text-green-700 text-center flex items-center justify-center gap-2">
                <i class="fa-solid fa-circle-check"></i> Salaire reçu
                <button @click="toggleRecu" class="text-xs text-gray-500 underline ml-2">annuler</button>
              </div>
            </div>
          </div>

          <div v-if="ownFiche.chargeDetail && ownFiche.chargeDetail.length" class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-sm font-bold text-gray-700 mb-3">Détail des charges</h3>
            <div v-for="c in ownFiche.chargeDetail" :key="c._id" class="flex justify-between py-2 border-b border-gray-50 text-sm">
              <span class="text-gray-600">{{ c.libelle }}</span><span class="text-red-500">{{ formatMoney(c.montant) }}</span>
            </div>
          </div>
        </div>

        <div v-else class="text-center w-full h-[300px] flex flex-col justify-center items-center">
          <i class="fa-solid fa-money-bill-wave text-4xl text-gray-300 mb-4"></i>
          <h2 class="text-lg font-semibold text-gray-500">Aucune fiche pour ce mois</h2>
        </div>
      </template>

      <!-- MODAL TAUX -->
      <div v-if="showTauxModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Taux de commission (%)</h3>
              <button @click="showTauxModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <form @submit.prevent="saveTaux" class="space-y-4">
              <div><label class="block text-sm font-medium text-gray-700 mb-1">iOS (%)</label><input v-model="tauxForm.ios" type="number" step="0.01" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"></div>
              <div><label class="block text-sm font-medium text-gray-700 mb-1">Android (%)</label><input v-model="tauxForm.android" type="number" step="0.01" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"></div>
              <div><label class="block text-sm font-medium text-gray-700 mb-1">Accessoires (%)</label><input v-model="tauxForm.accessoires" type="number" step="0.01" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"></div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center"><span v-if="isSubmitting" class="loader-small mr-2"></span> Enregistrer</button>
            </form>
          </div>
        </div>
      </div>

      <!-- MODAL FIXE -->
      <div v-if="showFixeModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Salaire fixe</h3>
              <button @click="showFixeModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <p class="text-sm text-gray-500 mb-4">{{ fixeTarget?.employe.prenoms }} {{ fixeTarget?.employe.nom }}</p>
            <form @submit.prevent="saveFixe" class="space-y-4">
              <div><label class="block text-sm font-medium text-gray-700 mb-1">Montant (FCFA)</label><input v-model="fixeValue" type="number" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" required></div>
              <button type="submit" :disabled="isSubmitting" class="w-full bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center"><span v-if="isSubmitting" class="loader-small mr-2"></span> Enregistrer</button>
            </form>
          </div>
        </div>
      </div>

      <!-- MODAL NET AJUSTÉ -->
      <div v-if="showNetModal" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md">
          <div class="p-6">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-xl font-bold text-gray-800">Ajuster le net</h3>
              <button @click="showNetModal = false" class="text-gray-400 hover:text-gray-600"><i class="fa-solid fa-times"></i></button>
            </div>
            <p class="text-sm text-gray-500 mb-1">{{ netTarget?.employe.prenoms }} {{ netTarget?.employe.nom }}</p>
            <div class="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
              <div class="flex justify-between"><span class="text-gray-500">Ce qui doit être (calculé)</span><span class="font-semibold text-gray-800">{{ formatMoney(netTarget?.salaireNet) }}</span></div>
            </div>
            <form @submit.prevent="saveNet" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ce qui est payé (net ajusté)</label>
                <input v-model="netValue" type="number" min="0" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500" placeholder="Laisser vide = utiliser le calculé">
                <p class="text-xs text-gray-400 mt-1">L'employé ne verra que ce montant.</p>
              </div>
              <div class="flex gap-2">
                <button type="button" @click="resetNet" class="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">Réinitialiser</button>
                <button type="submit" :disabled="isSubmitting" class="flex-1 bg-orange-500 text-white py-2 rounded-lg font-medium hover:bg-orange-600 flex justify-center items-center"><span v-if="isSubmitting" class="loader-small mr-2"></span> Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      </div>

    </section>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import HeaderComponent from '../components/HeaderComponent.vue';
import axios from 'axios';
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const user = computed(() => authStore.user);
const isSuper = computed(() => !!user.value?.isSuper);

function currentMonthStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const mois = ref(currentMonthStr());
const isLoading = ref(true);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const fiches = ref([]);
const recap = ref({ nombre: 0, totalSalaires: 0, totalCommissions: 0, totalCharges: 0, totalFixe: 0 });
const taux = ref({ ios: 0, android: 0, accessoires: 0 });
const ownFiche = ref(null);

const showTauxModal = ref(false);
const tauxForm = ref({ ios: 0, android: 0, accessoires: 0 });
const showFixeModal = ref(false);
const fixeTarget = ref(null);
const fixeValue = ref(0);
const showNetModal = ref(false);
const netTarget = ref(null);
const netValue = ref('');

function formatMoney(n) { return `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(n) || 0))} F`; }
function statutLabel(s) { return s === 'paye' ? 'Payé' : s === 'cloture' ? 'Clôturé' : 'En cours'; }
function statutClass(s) {
  if (s === 'paye') return 'bg-green-100 text-green-700';
  if (s === 'cloture') return 'bg-blue-100 text-blue-700';
  return 'bg-orange-100 text-orange-700';
}
const effectiveNet = (f) => (f.salaireNetEffectif != null ? f.salaireNetEffectif : (f.salaireNetAjuste != null ? f.salaireNetAjuste : f.salaireNet));
const isAjuste = (f) => f.salaireNetAjuste != null && f.salaireNetAjuste !== f.salaireNet;
function showSuccess(m) { successMessage.value = m; setTimeout(() => (successMessage.value = ''), 5000); }
function showError(m) { errorMessage.value = m; setTimeout(() => (errorMessage.value = ''), 8000); }

async function fetchSalaires() {
  isLoading.value = true;
  try {
    const { data } = await axios.get('/api/salaires', { params: { mois: mois.value } });
    fiches.value = data.fiches || [];
    recap.value = data.recap || recap.value;
    taux.value = data.taux || taux.value;
  } catch (e) { showError('Erreur lors du chargement des salaires.'); }
  finally { isLoading.value = false; }
}
async function fetchOwnFiche() {
  isLoading.value = true;
  try {
    const { data } = await axios.get(`/api/salaires/${user.value._id}`, { params: { mois: mois.value } });
    ownFiche.value = data.fiche || null;
  } catch (e) { showError('Erreur lors du chargement de votre salaire.'); }
  finally { isLoading.value = false; }
}

function openTauxModal() { tauxForm.value = { ...taux.value }; showTauxModal.value = true; }
async function saveTaux() {
  isSubmitting.value = true;
  try { await axios.put('/api/salaires/config', { taux: tauxForm.value }); showTauxModal.value = false; showSuccess('Taux mis à jour.'); await fetchSalaires(); }
  catch (e) { showError('Erreur lors de la mise à jour des taux.'); }
  finally { isSubmitting.value = false; }
}

function openFixeModal(fiche) { fixeTarget.value = fiche; fixeValue.value = fiche.salaireFixe || 0; showFixeModal.value = true; }
async function saveFixe() {
  isSubmitting.value = true;
  try { await axios.put(`/api/salaires/${fixeTarget.value.employe._id}/fixe`, { salaireFixe: fixeValue.value, mois: mois.value }); showFixeModal.value = false; showSuccess('Salaire fixe mis à jour.'); await fetchSalaires(); }
  catch (e) { showError('Erreur lors de la mise à jour du salaire fixe.'); }
  finally { isSubmitting.value = false; }
}

function openNetModal(fiche) { netTarget.value = fiche; netValue.value = fiche.salaireNetAjuste != null ? fiche.salaireNetAjuste : ''; showNetModal.value = true; }
async function saveNet() {
  isSubmitting.value = true;
  try {
    await axios.put(`/api/salaires/${netTarget.value.employe._id}/net`, { salaireNetAjuste: netValue.value === '' ? null : netValue.value, mois: mois.value });
    showNetModal.value = false; showSuccess('Net ajusté.'); await fetchSalaires();
  } catch (e) { showError('Erreur lors de l\'ajustement du net.'); }
  finally { isSubmitting.value = false; }
}
async function resetNet() { netValue.value = ''; await saveNet(); }

async function toggleCommission(fiche) {
  try { await axios.put(`/api/salaires/${fiche.employe._id}/commission`, { mois: mois.value }); await fetchSalaires(); }
  catch (e) { showError(e.response?.data?.error || 'Erreur commission.'); }
}

function categorieActive(fiche, categorie) {
  return fiche.categoriesActives ? fiche.categoriesActives[categorie] !== false : true;
}
async function toggleCategorie(fiche, categorie) {
  try { await axios.put(`/api/salaires/${fiche.employe._id}/categorie`, { mois: mois.value, categorie }); await fetchSalaires(); }
  catch (e) { showError(e.response?.data?.error || 'Erreur catégorie.'); }
}

async function markAsPaid(fiche) {
  const payer = fiche.statut !== 'paye';
  const nom = `${fiche.employe.prenoms} ${fiche.employe.nom}`;
  if (payer && !confirm(`Marquer le salaire de ${nom} comme payé ?`)) return;
  if (!payer && !confirm(`Annuler le paiement du salaire de ${nom} ?`)) return;
  try { await axios.put(`/api/salaires/${fiche.employe._id}/payer`, { mois: mois.value, payer }); await fetchSalaires(); showSuccess(payer ? 'Salaire marqué payé.' : 'Paiement annulé.'); }
  catch (e) { showError('Erreur lors du paiement.'); }
}
async function markAllAsPaid() {
  if (!confirm('Marquer TOUS les salaires affichés de ce mois comme payés ?')) return;
  try { const { data } = await axios.put('/api/salaires/payer-tout', { mois: mois.value }); await fetchSalaires(); showSuccess(data.message || 'Salaires marqués payés.'); }
  catch (e) { showError('Erreur lors du paiement groupé.'); }
}

// Employé : confirmer réception
async function toggleRecu() {
  try { await axios.put(`/api/salaires/${user.value._id}/recu`, { mois: mois.value }); await fetchOwnFiche(); }
  catch (e) { showError(e.response?.data?.error || 'Erreur.'); }
}

function loadForCurrentMode() {
  if (isSuper.value) fetchSalaires();
  else fetchOwnFiche();
}
watch(mois, () => loadForCurrentMode());
onMounted(() => loadForCurrentMode());
</script>

<style scoped>
.loader { border: 3px solid #f3f3f3; border-top: 3px solid #f97316; border-radius: 50%; width: 30px; height: 30px; animation: spin 1s linear infinite; }
.loader-small { border: 2px solid #f3f3f3; border-top: 2px solid #fff; border-radius: 50%; width: 16px; height: 16px; animation: spin 1s linear infinite; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
